import { useCallback, useEffect, useRef, useState } from "react";
import type { FaceLandmarker as FaceLandmarkerType } from "@mediapipe/tasks-vision";
import { DEFAULT_CONTROL_SETTINGS } from "../controlDefaults";
import type { CalibrationProfile, Pose, TrackingStatus, TriggerMode } from "../types";

type BlendScores = { mouth: number; blink: number };

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: any) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
};

const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
const WASM_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.22-rc.20250304/wasm";

function clamp(value: number) {
  return Math.max(-1, Math.min(1, value));
}

export function mapWebcamPose(raw: Pose, profile: CalibrationProfile | null): Pose {
  if (!profile) return { x: 0, y: 0, confidence: raw.confidence };
  const dx = raw.x - profile.neutral.x;
  const dy = raw.y - profile.neutral.y;
  const xRange = dx < 0 ? profile.range.left : profile.range.right;
  const yRange = dy < 0 ? profile.range.up : profile.range.down;
  const x = Math.abs(dx) < 0.008 ? 0 : dx / Math.max(0.025, xRange);
  const y = Math.abs(dy) < 0.008 ? 0 : -dy / Math.max(0.025, yRange);
  const mappedX = x * profile.sensitivity.x * (profile.flip.x ? -1 : 1) || 0;
  const mappedY = y * profile.sensitivity.y * (profile.flip.y ? -1 : 1) || 0;
  const neutralDepth = profile.neutral.z ?? raw.z ?? 0.24;
  const depthDelta = ((raw.z ?? neutralDepth) - neutralDepth) / Math.max(0.08, neutralDepth);
  return { x: clamp(mappedX), y: clamp(mappedY), z: clamp(depthDelta * 2.2), confidence: raw.confidence };
}

export function useFaceControls(
  profile: CalibrationProfile | null,
  triggerMode: TriggerMode,
) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const landmarkerRef = useRef<FaceLandmarkerType | null>(null);
  const frameRef = useRef<number | null>(null);
  const lastVideoTimeRef = useRef(-1);
  const lastSeenRef = useRef(0);
  const lastUiUpdateRef = useRef(0);
  const rawRef = useRef<Pose>({ x: 0.5, y: 0.5, confidence: 0 });
  const poseRef = useRef<Pose>({ x: 0, y: 0, confidence: 0 });
  const blendRef = useRef<BlendScores>({ mouth: 0, blink: 0 });
  const mouthArmedRef = useRef(true);
  const blinkStartedRef = useRef(0);
  const blinkArmedRef = useRef(true);
  const triggerModeRef = useRef(triggerMode);
  const profileRef = useRef(profile);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const requestIdRef = useRef(0);

  const [status, setStatus] = useState<TrackingStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [rawPose, setRawPose] = useState(rawRef.current);
  const [pose, setPose] = useState(poseRef.current);
  const [blend, setBlend] = useState(blendRef.current);
  const [fireSignal, setFireSignal] = useState(0);
  const [lastTrigger, setLastTrigger] = useState<TriggerMode | null>(null);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  useEffect(() => {
    triggerModeRef.current = triggerMode;
  }, [triggerMode]);

  const emitFire = useCallback((source: TriggerMode) => {
    setLastTrigger(source);
    setFireSignal((value) => value + 1);
  }, []);

  const evaluateTrigger = useCallback(
    (scores: BlendScores, now: number) => {
      const thresholds = profileRef.current?.thresholds ?? DEFAULT_CONTROL_SETTINGS.thresholds;
      if (triggerModeRef.current === "mouth") {
        if (scores.mouth < thresholds.mouthRelease) mouthArmedRef.current = true;
        if (scores.mouth > thresholds.mouthFire && mouthArmedRef.current) {
          mouthArmedRef.current = false;
          emitFire("mouth");
        }
      }

      if (triggerModeRef.current === "blink") {
        if (scores.blink > thresholds.blinkFire && blinkArmedRef.current && !blinkStartedRef.current) {
          blinkStartedRef.current = now;
        }
        if (scores.blink > thresholds.blinkFire && blinkArmedRef.current && blinkStartedRef.current) {
          const heldFor = now - blinkStartedRef.current;
          if (heldFor >= thresholds.blinkHoldMs && heldFor <= 800) {
            blinkArmedRef.current = false;
            blinkStartedRef.current = 0;
            emitFire("blink");
          } else if (heldFor > 800) {
            blinkStartedRef.current = 0;
          }
        }
        if (scores.blink < thresholds.blinkRelease) {
          blinkStartedRef.current = 0;
          blinkArmedRef.current = true;
        }
      }
    },
    [emitFire],
  );

  const processFrame = useCallback(() => {
    const video = videoRef.current;
    const landmarker = landmarkerRef.current;
    if (!video || !landmarker || video.readyState < 2) {
      frameRef.current = requestAnimationFrame(processFrame);
      return;
    }

    const now = performance.now();
    if (video.currentTime !== lastVideoTimeRef.current) {
      lastVideoTimeRef.current = video.currentTime;
      try {
        const result = landmarker.detectForVideo(video, now);
        const landmarks = result.faceLandmarks[0];
        if (landmarks?.length) {
          let minX = 1;
          let maxX = 0;
          let minY = 1;
          let maxY = 0;
          for (const point of landmarks) {
            minX = Math.min(minX, point.x);
            maxX = Math.max(maxX, point.x);
            minY = Math.min(minY, point.y);
            maxY = Math.max(maxY, point.y);
          }
          const nextRaw: Pose = {
            x: (minX + maxX) / 2,
            y: (minY + maxY) / 2,
            z: maxX - minX,
            confidence: 1,
          };
          const targetPose = mapWebcamPose(nextRaw, profileRef.current);
          const nextPose = {
            x: targetPose.x,
            y: targetPose.y,
            z: targetPose.z,
            confidence: 1,
          };

          const categories = result.faceBlendshapes[0]?.categories ?? [];
          const score = (name: string) => categories.find((item) => item.categoryName === name)?.score ?? 0;
          const nextBlend = {
            mouth: score("jawOpen"),
            blink: (score("eyeBlinkLeft") + score("eyeBlinkRight")) / 2,
          };

          rawRef.current = nextRaw;
          poseRef.current = nextPose;
          blendRef.current = nextBlend;
          lastSeenRef.current = now;
          setStatus("tracking");
          evaluateTrigger(nextBlend, now);

          if (now - lastUiUpdateRef.current > 70) {
            lastUiUpdateRef.current = now;
            setRawPose(nextRaw);
            setPose(nextPose);
            setBlend(nextBlend);
          }
        } else if (now - lastSeenRef.current > 700) {
          setStatus("lost");
        }
      } catch (frameError) {
        console.warn("Face tracking frame failed", frameError);
      }
    }

    frameRef.current = requestAnimationFrame(processFrame);
  }, [evaluateTrigger]);

  const startCamera = useCallback(async () => {
    if (streamRef.current && landmarkerRef.current) return;
    const requestId = ++requestIdRef.current;
    setError(null);
    setStatus("requesting");
    let cameraStarted = false;
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new DOMException("Camera APIs require a secure browser context.", "SecurityError");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: false,
      });
      cameraStarted = true;
      if (requestId !== requestIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playError) {
          if (!(playError instanceof DOMException) || playError.name !== "AbortError") throw playError;
        }
      }

      setStatus("loading-model");
      const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
      const vision = await FilesetResolver.forVisionTasks(WASM_URL);
      try {
        landmarkerRef.current = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
          runningMode: "VIDEO",
          numFaces: 1,
          outputFaceBlendshapes: true,
        });
      } catch {
        landmarkerRef.current = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: "CPU" },
          runningMode: "VIDEO",
          numFaces: 1,
          outputFaceBlendshapes: true,
        });
      }
      if (requestId !== requestIdRef.current) {
        void landmarkerRef.current?.close();
        landmarkerRef.current = null;
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      lastSeenRef.current = performance.now();
      frameRef.current = requestAnimationFrame(processFrame);
    } catch (cameraError) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      void landmarkerRef.current?.close();
      landmarkerRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      const errorName = cameraError instanceof DOMException ? cameraError.name : "UnknownError";
      const isDenied = errorName === "NotAllowedError";
      setStatus(isDenied ? "denied" : "error");
      if (cameraStarted) {
        setError("The camera started, but face tracking could not load. Check your connection and try again.");
      } else if (isDenied) {
        setError("Camera access was blocked. Allow camera access in your browser, then try again.");
      } else if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
        setError("No camera was found. Connect or enable a camera, then try again.");
      } else if (errorName === "NotReadableError" || errorName === "TrackStartError") {
        setError("The camera is busy or unavailable. Close other camera apps, then try again.");
      } else if (errorName === "SecurityError") {
        setError("Camera access requires a secure HTTPS connection and browser permission.");
      } else {
        setError("The camera could not be started. Check browser camera permissions, then try again.");
      }
      console.warn("Camera or face-tracking startup failed", { phase: cameraStarted ? "face-tracking" : "camera", errorName, cameraError });
    }
  }, [processFrame]);

  const stopCamera = useCallback(() => {
    requestIdRef.current += 1;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void landmarkerRef.current?.close();
    landmarkerRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setStatus("idle");
  }, []);

  const setVideoElement = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
      void node.play().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    if (triggerMode !== "voice") {
      recognitionRef.current?.stop();
      recognitionRef.current = null;
      return;
    }
    const SpeechRecognitionCtor = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return;

    const recognition = new SpeechRecognitionCtor() as SpeechRecognitionLike;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onresult = (event: any) => {
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = event.results[index][0]?.transcript?.toLowerCase() ?? "";
        if (/\b(pew|phew|few)\b/.test(transcript)) emitFire("voice");
      }
    };
    recognition.onend = () => {
      if (triggerModeRef.current === "voice") {
        try {
          recognition.start();
        } catch {
          // Browser can reject an immediate restart; the next settings change retries.
        }
      }
    };
    recognition.onerror = () => undefined;
    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      // Unsupported or permission-blocked voice remains a non-fatal optional input.
    }
    return () => {
      recognition.onend = null;
      recognition.stop();
      recognitionRef.current = null;
    };
  }, [emitFire, triggerMode]);

  useEffect(() => stopCamera, [stopCamera]);

  const voiceSupported = Boolean(
    (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition,
  );

  return {
    videoRef,
    setVideoElement,
    status,
    error,
    rawPose,
    pose,
    blend,
    fireSignal,
    lastTrigger,
    voiceSupported,
    startCamera,
    stopCamera,
    demoFire: () => emitFire(triggerMode),
  };
}
